import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPayrollAuditLog } from "services/payrolls";

const ACTION_COLORS = {
    approved:  { bg: "#d1fae5", color: "#065f46" },
    created:   { bg: "#dbeafe", color: "#1e40af" },
    updated:   { bg: "#fef9c3", color: "#92400e" },
    reopened:  { bg: "#fee2e2", color: "#991b1b" },
};

const badge = (action) => {
    const s = ACTION_COLORS[action] || { bg: "#f3f4f6", color: "#374151" };
    return (
        <span style={{ background: s.bg, color: s.color, padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {action}
        </span>
    );
};

const fmt = (iso) => {
    if (!iso) return "—";
    // Append Z so UTC timestamps are correctly converted to local browser time
    const utc = iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z";
    const d = new Date(utc);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
};

const AuditTrail = () => {
    const [search, setSearch] = useState("");

    const { data: logs = [], isLoading, error, refetch } = useQuery({
        queryKey: ["payroll-audit-log"],
        queryFn: () => getPayrollAuditLog({ limit: 500 }),
        staleTime: 30 * 1000,
    });

    const filtered = logs.filter(l => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            (l.payrollId || "").toLowerCase().includes(q) ||
            (l.action || "").toLowerCase().includes(q) ||
            (l.actorEmail || "").toLowerCase().includes(q) ||
            (l.notes || "").toLowerCase().includes(q)
        );
    });

    return (
        <div style={{ padding: "8px 4px" }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                <div>
                    <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Payroll Audit Trail</h2>
                    <p style={{ fontSize: 12, color: "#666", margin: "2px 0 0" }}>Every payroll action logged with timestamp and actor</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search by payroll ID, action, email…"
                        style={{ fontSize: 13, border: "1px solid #ddd", borderRadius: 6, padding: "6px 12px", width: 260 }}
                    />
                    <button onClick={refetch} style={{ fontSize: 12, border: "1px solid #ddd", borderRadius: 6, padding: "6px 12px", background: "#fff", cursor: "pointer" }}>
                        ↻ Refresh
                    </button>
                </div>
            </div>

            {isLoading && <p style={{ color: "#888", fontSize: 13 }}>Loading audit log…</p>}
            {error && <p style={{ color: "#c00", fontSize: 13 }}>Failed to load audit log: {error.message}</p>}

            {!isLoading && !error && filtered.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa", fontSize: 13 }}>
                    {search ? "No entries match your search." : "No audit log entries yet. Approve or modify a payroll to generate entries."}
                </div>
            )}

            {!isLoading && filtered.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e5e7eb" }}>
                                {["Timestamp", "Action", "Payroll ID", "Actor", "Notes"].map(h => (
                                    <th key={h} style={{ textAlign: "left", padding: "10px 12px", fontWeight: 700, fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((log, idx) => (
                                <tr key={log.id || idx} style={{ borderBottom: "1px solid #f0f0f0", background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: "#374151" }}>{fmt(log.createdAt)}</td>
                                    <td style={{ padding: "10px 12px" }}>{badge(log.action)}</td>
                                    <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: 11, color: "#555", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                        title={log.payrollId}>
                                        {log.payrollId || "—"}
                                    </td>
                                    <td style={{ padding: "10px 12px", color: "#374151" }}>{log.actorEmail || "—"}</td>
                                    <td style={{ padding: "10px 12px", color: "#6b7280", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                        title={log.notes}>
                                        {log.notes || "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>Showing {filtered.length} of {logs.length} entries</p>
                </div>
            )}
        </div>
    );
};

export default AuditTrail;
